import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import styled from 'styled-components';
import { useViewport } from 'Common/hooks';
import { LinkItem } from 'Common/components/ui';
import { LinkItem as LinkItemType } from 'Common/types';

const Container = styled.div`
    align-items: ${({ horizontalAlign }) => {
        switch (horizontalAlign) {
            case 'right':
                return 'flex-end';
            case 'middle':
            case 'center':
                return 'center';
            default:
                return 'flex-start';
        }
    }};
    text-align: ${({ horizontalAlign }) => horizontalAlign};
    justify-content: ${({ verticalAlign }) => {
        switch (verticalAlign) {
            case 'bottom':
                return 'flex-end';
            case 'middle':
            case 'center':
                return 'center';
            case 'span':
            case 'stretch':
                return 'space-between';
            default:
                return 'flex-start';
        }
    }};
    background-image: ${({ imageUrl }) => `url(${imageUrl})`};
    background-position: center center;
    background-repeat: no-repeat;
    background-size: ${({ imageResize }) => {
        switch (imageResize) {
            case 'fit':
            case 'contain':
                return 'contain';
            case 'cover':
                return 'cover';
            case 'stretch':
                return '100% 100%';
            default:
                return 'auto auto';
        }
    }};
`;

function goto(link) {
    if (!link?.href) return undefined;

    return () => {
        if (link.target === '_blank') {
            window.open(link.href, link.target, 'noopener,noreferrer');
        } else {
            window.location.href = link.href;
        }
    };
}

export default function Default({
    className,
    model: {
        SubTitle,
        Title = 'Call To Action',
        Details,
        HorizontalAlign = 'center',
        MobileHorizontalAlign,
        VerticalAlign = 'center',
        MobileVerticalAlign,
        ImageUrl,
        ImageResize = 'cover',
        ButtonText,
        ButtonIcon,
        ButtonIconPosition = 'last',
        TextColor = 'light',
        LinkItem: Link,
    } = {},
}) {
    const viewport = useViewport();
    const vAlign = React.useMemo(
        () => (viewport.is.mobile && MobileVerticalAlign) || VerticalAlign,
        [viewport, MobileVerticalAlign, VerticalAlign]
    );

    const hAlign = React.useMemo(
        () => (viewport.is.mobile && MobileHorizontalAlign) || HorizontalAlign,
        [viewport, MobileHorizontalAlign, HorizontalAlign]
    );

    return (
        <Container
            className={cx('DefaultCatalogCTA', className, `text-color-${TextColor}`)}
            role={Link?.href && !ButtonText ? 'button' : 'card'}
            horizontalAlign={hAlign}
            verticalAlign={vAlign}
            imageResize={ImageResize}
            imageUrl={ImageUrl}
            onClick={goto(!ButtonText && Link)}
        >
            {SubTitle ? <h6>{SubTitle}</h6> : null}
            {Title ? <h3>{Title}</h3> : null}
            {Details ? <p className="paragraph-2">{Details}</p> : null}
            {(ButtonText || ButtonIcon) && Link ? (
                <LinkItem role="button" className="btn btn-md btn-primary" link={Link}>
                    {ButtonIcon && ButtonIconPosition?.toLowerCase() === 'first' ? (
                        <i className={ButtonIcon} aria-hidden="true" />
                    ) : null}
                    <span>{ButtonText}</span>
                    {ButtonIcon && ButtonIconPosition?.toLowerCase() === 'last' ? (
                        <i className={ButtonIcon} aria-hidden="true" />
                    ) : null}
                </LinkItem>
            ) : null}
        </Container>
    );
}

Default.propTypes = {
    className: px.string,
    model: px.shape({
        SubTitle: px.string,
        Title: px.string,
        Details: px.string,
        HorizontalAlign: px.string,
        VerticalAlign: px.string,
        MobileHorizontalAlign: px.string,
        MobileVerticalAlign: px.string,
        ImageResize: px.string,
        ImageUrl: px.string,
        ImageFormat: px.string,
        ButtonText: px.string,
        ButtonIcon: px.string,
        ButtonIconOrder: px.string,
        TextColor: px.string,
        LinkItem: LinkItemType,
    }),
};
